import { CircularProgress } from '@mui/material'
import { useEffect, useState } from 'react'
import { type postError, type location } from 'core/types'
import 'Components/Cards/LocationCard/LocationCard.css'
import { LocationCardDisplay } from 'Components/Cards/LocationCard/LocationCardDisplay'
import { sendGet, sendPost, sendPut } from 'hooks/use-fetch'
import { toast } from 'react-toastify'

interface LocationCardProps {
  locationId: number
  childId: number
  isPrimary: boolean
  deleteDisabled?: boolean
  readOnly?: boolean
  onDelete?: () => void
  setRefreshTime: React.Dispatch<React.SetStateAction<Date>>
}

export function LocationCard (props: LocationCardProps): JSX.Element {
  const [edit, setEdit] = useState(props.locationId === 0)
  const [editData, setEditData] = useState<location | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [refreshTime, setRefreshTime] = useState(new Date())

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response: rsp } = await sendGet(`/CareNetworkEdit/GetLocation?id=${props.locationId}`, {})
      if (rsp !== null) {
        const data = rsp as location
        data.isPrimary = props.isPrimary
        setEditData(data)
      }
    }
    void loadData()
  }, [props.locationId, refreshTime])

  if (editData === null) return <CircularProgress />

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
    setEdit(false)
  }

  const openEdit = (): void => {
    setEditData(editData)
    setEdit(true)
    setAnchorEl(null)
  }

  const handleCancel = (): void => {
    setEdit(false)
  }

  const onDelete = async (): Promise<void> => {
    const submitData: { locationId: number } = { locationId: editData.id }
    const { response, error } = await sendPost('/CareNetworkEdit/DeleteLocation', submitData)
    if (response as boolean) {
      setRefreshTime(new Date())
      props.onDelete?.()
      setEdit(false)
    } else {
      error?.forEach((e) => {
        toast.error(e)
      })
    }
  }

  const onUpdate = async (updatedData: location): Promise<void> => {
    const submitData: { entityId: number, Location: location, isPrimary: boolean } = { entityId: props.childId, Location: updatedData, isPrimary: props.isPrimary }
    const { response: rsp } = await sendPut('/CareNetworkEdit/UpdateLocation', submitData)
    const result: { newRecordID: number, errors: postError[], success: boolean } = rsp
    if (result.success) {
      setRefreshTime(new Date())
      setEdit(false)
    } else {
      result.errors?.forEach((e) => {
        toast.error(e.error)
      })
    }
  }

  return (
    <LocationCardDisplay
      isPrimary={props.isPrimary}
      showHomelessOption={true}
      data={editData}
      childID={props.childId}
      edit={edit}
      readOnly={props.readOnly}
      anchorEl={anchorEl}
      onClick={handleClick} onClose={handleClose}
      onEdit={openEdit} onDelete={onDelete} onUpdate={onUpdate} onCancel={handleCancel}
    />
  )
}
